import {skipToken} from '@reduxjs/toolkit/query';
import {Tooltip, useDialog} from 'platform/components';
import {HStack, Link, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useMemo, useState} from 'react';

// eslint-disable-next-line no-restricted-imports -- Intended
import {defaultTo, difference, indexBy, isNil, pathOr, prop} from 'ramda';
import {isArray, isString, isTrue} from 'ramda-adjunct';

import {
  useAssignAssigneeFromTeamMutation,
  useAssigneeTeamListQuery,
  useAssignOwnerFromTeamMutation,
  useAssignUserV2Mutation,
  useGetParticipationQuery,
  useOwnerTeamListQuery,
  useTransferOwnershipToUserV2Mutation,
  useUnassignUserV2Mutation,
} from '@dms/api/core';
import {useGetUsersQuery} from '@dms/api/customer';
import {
  EntityResourceIds,
  GetParticipationApiResponse,
  UserStateFilterSchema,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {HeaderPermissions} from '../EntityHeader/types/headerPermissions';
import {NoPermissionTooltip} from '../NoPermissionTooltip/NoPermissionTooltip';
import {AssigneeButton} from './components/AssigneeButton';
import {EditUserDialog} from './components/EditUserDialog';
import {AssignmentSelection} from './types/AssignmentSelection';
import {getTeamId, isTeamValue} from './utils/teamValueHelpers';

interface AssigneeV2Props extends TestIdProps {
  resourceId: EntityResourceIds;
  recordId: string;
  participationPermissions: HeaderPermissions['participation'];
  enableTeams?: boolean;
}

export function AssigneeV2(props: AssigneeV2Props) {
  const {data: participation, refetch: refetchParticipation} = useGetParticipationQuery(props);
  const {data: users} = useGetUsersQuery();
  const {data: activeUsers, isLoading: isUsersLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });

  const teamQueryArg = props.enableTeams
    ? {resourceId: props.resourceId, recordId: props.recordId}
    : skipToken;
  const {data: assigneeTeams, isLoading: isAssigneeTeamsLoading} =
    useAssigneeTeamListQuery(teamQueryArg);
  const {data: ownerTeams, isLoading: isOwnerTeamsLoading} = useOwnerTeamListQuery(teamQueryArg);

  const [transferOwnership] = useTransferOwnershipToUserV2Mutation();
  const [assignUser] = useAssignUserV2Mutation();
  const [unAssignUser] = useUnassignUserV2Mutation();
  const [assignAssigneeFromTeam] = useAssignAssigneeFromTeamMutation();
  const [assignOwnerFromTeam] = useAssignOwnerFromTeamMutation();

  const {canAssignUser, canChangeOwner, canUnassignUser} = props.participationPermissions ?? {};

  const canChangeVehicleAssignee =
    (isNil(canUnassignUser) && isNil(canAssignUser)) ||
    (isTrue(canUnassignUser) && isTrue(canAssignUser));

  const canChangeOwnership = isNil(canChangeOwner) || isTrue(canChangeOwner);

  const usersById = useMemo(() => indexBy(prop('id'), users ?? []), [users]);

  const [assignmentSelection, setAssignmentSelection] = useState<AssignmentSelection | null>(null);
  const [isOpen, setOpen, setClose] = useDialog();
  const [isValidationError, setValidationErrorTrue, setValidationErrorFalse] = useBoolean();

  const handleOpenDialog = (type: keyof GetParticipationApiResponse) => {
    const ParticipationType = participation?.[type];

    const stateBody: AssignmentSelection = {
      value: isArray(ParticipationType)
        ? ParticipationType.map(({userId}) => userId)
        : (ParticipationType?.userId ?? ''),
      type,
    };

    setAssignmentSelection(stateBody);
    setOpen();
  };

  const saveOwnerAssignment = (value: string) => {
    if (isTeamValue(value)) {
      return assignOwnerFromTeam({
        recordId: props.recordId,
        resourceId: props.resourceId,
        assignOwnerFromTeamRequestBody: {teamId: getTeamId(value)},
      })
        .unwrap()
        .then(() => {
          refetchParticipation();
        })
        .catch(handleApiError);
    }

    return transferOwnership({
      recordId: props.recordId,
      resourceId: props.resourceId,
      userIdToTransferOwnership: value,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const saveAssigneeAssignment = (selectedValues: string[]) => {
    const selectedTeamValue = selectedValues.find(isTeamValue);

    if (selectedTeamValue) {
      const legacyParticipationIds = participation?.assignees.map(({userId}) => userId) ?? [];

      const unassignExistingUsers = legacyParticipationIds.map((userIdToUnassign) =>
        unAssignUser({
          recordId: props.recordId,
          resourceId: props.resourceId,
          userIdToUnassign,
        }).unwrap()
      );

      return Promise.all(unassignExistingUsers)
        .then(() =>
          assignAssigneeFromTeam({
            recordId: props.recordId,
            resourceId: props.resourceId,
            assignAssigneeFromTeamRequestBody: {teamId: getTeamId(selectedTeamValue)},
          }).unwrap()
        )
        .then(() => {
          refetchParticipation();
        })
        .catch(handleApiError);
    }

    const legacyParticipationIds = participation?.assignees.map(({userId}) => userId) ?? [];

    const updates = difference(selectedValues, legacyParticipationIds);
    const removals = difference(legacyParticipationIds, selectedValues);

    const updatePromises = updates.map((userIdToAssign) =>
      assignUser({recordId: props.recordId, resourceId: props.resourceId, userIdToAssign}).unwrap()
    );
    const removalPromises = removals.map((userIdToUnassign) =>
      unAssignUser({
        recordId: props.recordId,
        resourceId: props.resourceId,
        userIdToUnassign,
      }).unwrap()
    );

    return Promise.all([...updatePromises, ...removalPromises])
      .then(() => {
        refetchParticipation();
      })
      .catch(handleApiError);
  };

  const saveAssignments = () => {
    if (!assignmentSelection) {
      return;
    }

    const assignmentValue = assignmentSelection.value;

    match(assignmentSelection.type)
      .with('owner', () => {
        if (!isString(assignmentValue)) {
          setValidationErrorTrue();
          return;
        }
        saveOwnerAssignment(assignmentValue);
        setClose();
      })
      .with('assignees', () => {
        if (isArray(assignmentValue)) {
          saveAssigneeAssignment(assignmentValue);
        }
        setClose();
      })
      .otherwise(() => {
        setClose();
      });
  };

  const activeTeams = match(assignmentSelection?.type)
    .with('owner', () => ownerTeams)
    .with('assignees', () => assigneeTeams)
    .otherwise(() => undefined);

  const isActiveTeamsLoading = match(assignmentSelection?.type)
    .with('owner', () => isOwnerTeamsLoading)
    .with('assignees', () => isAssigneeTeamsLoading)
    .otherwise(() => false);

  const isOptionsLoading = isUsersLoading || isActiveTeamsLoading;

  const owner = usersById[pathOr('', ['owner', 'userId'], participation)];

  const assigneesNames =
    participation?.assignees.map((assignee) =>
      defaultTo('', getNaturalPersonFullName(usersById[assignee.userId]))
    ) ?? [];

  return (
    <HStack align="center" spacing={1}>
      <NoPermissionTooltip shouldShowTooltip={!canChangeOwnership}>
        <Tooltip
          label={i18n.t('entity.controls.labels.participation.owner.tooltip')}
          isDisabled={!canChangeOwnership}
          childrenWrapperDisplay="flex"
        >
          <Link
            size="small"
            onClick={() => handleOpenDialog('owner')}
            leftIcon="maps/person_pin"
            title={defaultTo(
              i18n.t('general.labels.participation.systemOwner'),
              getNaturalPersonFullName(owner)
            )}
            isDisabled={!canChangeOwnership}
            data-testid={suffixTestId('systemOwner', props)}
          />
        </Tooltip>
      </NoPermissionTooltip>

      <Tooltip
        label={i18n.t('entity.controls.labels.participation.assignees.tooltip')}
        isDisabled={!canChangeVehicleAssignee || assigneesNames.length > 0}
        childrenWrapperDisplay="flex"
      >
        <AssigneeButton
          assigneesNames={assigneesNames}
          handleEdit={handleOpenDialog}
          isDisabled={!canChangeVehicleAssignee}
          data-testid={suffixTestId('assignees', props)}
        />
      </Tooltip>

      <Show when={assignmentSelection}>
        <EditUserDialog
          isOpen={isOpen}
          onClose={setClose}
          onSave={saveAssignments}
          assignmentSelection={assignmentSelection!}
          updateAssignmentSelection={setAssignmentSelection}
          users={activeUsers}
          teams={activeTeams}
          isLoading={isOptionsLoading}
          data-testid={suffixTestId('editUser', props)}
          deleteErrorMessage={setValidationErrorFalse}
          isErrorMessage={isValidationError}
        />
      </Show>
    </HStack>
  );
}
